import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Paper,
  Chip,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  AutoFixHigh as OptimizeIcon,
} from '@mui/icons-material';
import { api } from '../api';
import PromptOptimizationModal from './PromptOptimizationModal';

// Route definition
interface Route {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

// Decision configuration
interface DecisionConfig {
  type: string;
  decisionPrompt: string;
  model: string;
  routes: Route[];
  passthrough: boolean;
  includeDecisionMetadata: boolean;
  defaultRoute?: string;
  strictMatching: boolean;
  maxInputTokens?: number | null;
}

interface DecisionBlockConfigPanelProps {
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}

// Test Decision Section Component
const TestDecisionSection: React.FC<{ config: DecisionConfig }> = ({ config }) => {
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<{ routeName: string; routeId: string; confidence: number; reasoning: string; color?: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async () => {
    if (!testInput.trim()) {
      alert('Please enter test input');
      return;
    }

    if (!config.decisionPrompt || config.routes.length < 2) {
      alert('Please configure decision prompt and at least 2 routes first');
      return;
    }

    setIsLoading(true);
    setError(null);
    setTestResult(null);

    try {
      // Build the decision prompt
      const routesList = config.routes.map(r => r.name).join(', ');
      const decisionPrompt = `${config.decisionPrompt}\n\nAvailable routes: ${routesList}\n\nChoose one of the routes based on the input. Respond with only the route name.`;

      // Call the API with prompt and input as separate parameters
      const result = await api.testPrompt(decisionPrompt, testInput, config.model);
      const response = result.response;
      
      // Try to match the response to a route
      let matchedRoute: Route | null = null;
      let confidence = 0;

      // Simple matching logic (similar to backend)
      const responseLower = response.toLowerCase();
      for (const route of config.routes) {
        const routeNameLower = route.name.toLowerCase();
        if (responseLower.includes(routeNameLower)) {
          matchedRoute = route;
          // Calculate confidence based on how directly it's mentioned
          const index = responseLower.indexOf(routeNameLower);
          confidence = index < 50 ? 0.9 : 0.7; // Higher confidence if mentioned early
          break;
        }
      }

      if (matchedRoute) {
        setTestResult({
          routeName: matchedRoute.name,
          routeId: matchedRoute.id,
          confidence,
          reasoning: response,
          color: matchedRoute.color
        });
      } else {
        setError(`Could not match response to any route. Response: "${response.substring(0, 100)}..."`);
      }
    } catch (err) {
      setError(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Test your decision logic with sample input to see which route would be chosen
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder="Enter test input text..."
        value={testInput}
        onChange={(e) => setTestInput(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        onClick={handleTest}
        disabled={isLoading || !config.decisionPrompt || config.routes.length < 2}
        sx={{ mb: 2 }}
      >
        {isLoading ? 'Testing...' : 'Test Decision'}
      </Button>

      {testResult && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: testResult.color ? `${testResult.color}20` : '#e8f5e9',
            border: `2px solid ${testResult.color || '#4CAF50'}`,
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body1" fontWeight="bold" sx={{ color: testResult.color || '#4CAF50' }}>
              → {testResult.routeName}
            </Typography>
            <Chip
              label={`${(testResult.confidence * 100).toFixed(0)}% confident`}
              size="small"
              sx={{ backgroundColor: testResult.color || '#4CAF50', color: 'white' }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" fontWeight="medium">
            Reasoning:
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {testResult.reasoning}
          </Typography>
        </Paper>
      )}

      {error && (
        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#ffebee', border: '2px solid #f44336', borderRadius: 2 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Show available routes */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight="medium">
          Available Routes:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {config.routes.map((route) => (
            <Chip
              key={route.id}
              label={route.name}
              sx={{
                backgroundColor: route.color,
                color: 'white',
                fontWeight: 'medium'
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const DecisionBlockConfigPanel: React.FC<DecisionBlockConfigPanelProps> = ({ config, onChange }) => {
  const [localConfig, setLocalConfig] = useState<DecisionConfig>({
    type: 'decision',
    decisionPrompt: config.decisionPrompt || 'Choose best LLM model to use based on the input text',
    model: config.model || 'gpt-4o-mini',
    routes: config.routes || [
      { id: 'route_1', name: 'Route 1', description: '', color: '#4CAF50' },
      { id: 'route_2', name: 'Route 2', description: '', color: '#2196F3' },
    ],
    passthrough: config.passthrough !== undefined ? config.passthrough : true,
    includeDecisionMetadata: config.includeDecisionMetadata || false,
    defaultRoute: config.defaultRoute || '',
    strictMatching: config.strictMatching || false,
    maxInputTokens: config.maxInputTokens || null,
  });

  const [modelOptions, setModelOptions] = useState<{id: string, name: string, provider?: string, displayName?: string}[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [optimizationModalOpen, setOptimizationModalOpen] = useState<boolean>(false);

  // Load models on component mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await api.getAllModels();
        const models = response.models || [];
        const filteredModels = models.filter((model: any) => !model.deprecated && model.enabled);
        const modelOptions = filteredModels.map((model: any) => ({
          id: model.id,
          name: model.name,
          provider: model.provider || 'unknown',
          displayName: `${(model.provider || 'unknown').toUpperCase()} / ${model.name}`,
        }));
        setModelOptions(modelOptions);
      } catch (error) {
        console.error('Failed to load models:', error);
      }
    };

    loadModels();
  }, []);

  // Handle configuration changes
  const handleConfigChange = (updatedConfig: Partial<DecisionConfig>) => {
    const newConfig = {
      ...localConfig,
      ...updatedConfig,
    };
    setLocalConfig(newConfig);
    onChange(newConfig as Record<string, any>);
  };

  // Add a new route
  const handleAddRoute = () => {
    const newRoute: Route = {
      id: `route_${Date.now()}`,
      name: `Route ${localConfig.routes.length + 1}`,
      description: '',
      color: `#${Math.floor(Math.random()*16777215).toString(16)}`, // Random color
    };
    const updatedRoutes = [...localConfig.routes, newRoute];
    handleConfigChange({ routes: updatedRoutes });
  };

  // Update a route
  const handleUpdateRoute = (index: number, field: keyof Route, value: string) => {
    const updatedRoutes = [...localConfig.routes];
    updatedRoutes[index] = {
      ...updatedRoutes[index],
      [field]: value,
    };
    handleConfigChange({ routes: updatedRoutes });
  };

  // Delete a route
  const handleDeleteRoute = (index: number) => {
    if (localConfig.routes.length <= 2) {
      alert('Decision blocks must have at least 2 routes');
      return;
    }
    const updatedRoutes = localConfig.routes.filter((_, i) => i !== index);
    handleConfigChange({ routes: updatedRoutes });
  };

  // Predefined colors
  const predefinedColors = [
    { name: 'Green', value: '#4CAF50' },
    { name: 'Blue', value: '#2196F3' },
    { name: 'Purple', value: '#9C27B0' },
    { name: 'Orange', value: '#FF9800' },
    { name: 'Red', value: '#F44336' },
    { name: 'Cyan', value: '#00BCD4' },
    { name: 'Yellow', value: '#FFEB3B' },
    { name: 'Pink', value: '#E91E63' },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ fontSize: '1.5rem' }}>🔀</span>
        Decision Block Configuration
      </Typography>
      
      <Divider sx={{ my: 2 }} />

      {/* Decision Prompt */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              Decision Prompt *
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Instructions for the LLM on how to analyze the input and choose a route.
            </Typography>
          </Box>
          <Tooltip title="Optimize this prompt using AI">
            <IconButton
              size="small"
              onClick={() => setOptimizationModalOpen(true)}
              disabled={!localConfig.decisionPrompt || localConfig.decisionPrompt.trim() === ''}
              color="primary"
            >
              <OptimizeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={localConfig.decisionPrompt}
          onChange={(e) => handleConfigChange({ decisionPrompt: e.target.value })}
          placeholder="Choose best LLM model to use based on the input text"
          variant="outlined"
        />
      </Box>

      {/* Model Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Decision Model *
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Choose a fast, cost-effective model for making routing decisions.
        </Typography>
        
        <Autocomplete
          fullWidth
          options={modelOptions}
          value={modelOptions.find((option) => option.id === localConfig.model) || null}
          onChange={(event, newValue) => {
            if (newValue) {
              handleConfigChange({ model: newValue.id });
            }
          }}
          getOptionLabel={(option) => option.displayName || option.name}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) return options;
            const searchLower = inputValue.toLowerCase();
            return options.filter((option) =>
              option.name.toLowerCase().includes(searchLower) ||
              (option.displayName && option.displayName.toLowerCase().includes(searchLower)) ||
              (option.provider && option.provider.toLowerCase().includes(searchLower))
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search models..."
              variant="outlined"
            />
          )}
          ListboxProps={{
            style: { maxHeight: 400 }
          }}
        />
      </Box>

      {/* Routes Configuration */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Routes ({localConfig.routes.length}) *
          </Typography>
          <Button 
            startIcon={<AddIcon />} 
            onClick={handleAddRoute}
            size="small"
            variant="outlined"
          >
            Add Route
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Define the possible routing options. Minimum 2 routes required.
        </Typography>

        {localConfig.routes.map((route, index) => (
          <Paper key={route.id} elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <DragIcon sx={{ color: 'text.disabled', cursor: 'move', mt: 1 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Route Name"
                    value={route.name}
                    onChange={(e) => handleUpdateRoute(index, 'name', e.target.value)}
                    required
                  />
                  <TextField
                    select
                    size="small"
                    label="Color"
                    value={route.color || '#4CAF50'}
                    onChange={(e) => handleUpdateRoute(index, 'color', e.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    {predefinedColors.map((color) => (
                      <MenuItem key={color.value} value={color.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              backgroundColor: color.value,
                              borderRadius: '50%',
                              border: '1px solid #ddd',
                            }}
                          />
                          {color.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Description (Optional)"
                  value={route.description || ''}
                  onChange={(e) => handleUpdateRoute(index, 'description', e.target.value)}
                  placeholder="When should this route be chosen?"
                />
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={`ID: ${route.id}`} 
                    size="small" 
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: route.color,
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                    }}
                  />
                </Box>
              </Box>
              <IconButton 
                onClick={() => handleDeleteRoute(index)}
                color="error"
                size="small"
                disabled={localConfig.routes.length <= 2}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Advanced Settings */}
      <Accordion expanded={advancedOpen} onChange={() => setAdvancedOpen(!advancedOpen)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight="bold">
            Advanced Settings
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {/* Passthrough */}
            <FormControlLabel
              control={
                <Switch
                  checked={localConfig.passthrough}
                  onChange={(e) => handleConfigChange({ passthrough: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Passthrough Original Input
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Send original input to the next block (recommended)
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 2 }} />

            {/* Include Metadata */}
            <FormControlLabel
              control={
                <Switch
                  checked={localConfig.includeDecisionMetadata}
                  onChange={(e) => handleConfigChange({ includeDecisionMetadata: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Include Decision Metadata
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add decision info (route chosen, confidence) to output
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 2 }} />

            {/* Default Route */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Default Route (Fallback)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Route to use if decision is unclear or fails
              </Typography>
              <TextField
                fullWidth
                select
                size="small"
                value={localConfig.defaultRoute || ''}
                onChange={(e) => handleConfigChange({ defaultRoute: e.target.value })}
              >
                <MenuItem value="">
                  <em>None (Error on failure)</em>
                </MenuItem>
                {localConfig.routes.map((route) => (
                  <MenuItem key={route.id} value={route.id}>
                    {route.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Strict Matching */}
            <FormControlLabel
              control={
                <Switch
                  checked={localConfig.strictMatching}
                  onChange={(e) => handleConfigChange({ strictMatching: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Strict Route Matching
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Require exact route name match (disable fuzzy matching)
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 2 }} />

            {/* Max Input Tokens */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Max Input Tokens (Cost Optimization)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Limit input text used for decision making to reduce costs and improve speed. The full output is still passed to the next block.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Unlimited"
                  value={localConfig.maxInputTokens || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleConfigChange({ 
                      maxInputTokens: value === '' ? null : Math.max(0, parseInt(value) || 0)
                    });
                  }}
                  inputProps={{ min: 0, step: 100 }}
                  sx={{ width: 200 }}
                />
                <Chip 
                  label={localConfig.maxInputTokens ? `~${(localConfig.maxInputTokens * 4).toLocaleString()} chars` : 'No limit'} 
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                Leave empty for no limit. Recommended: 500-2000 tokens for most use cases.
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Test Decision Logic */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>⚡</span>
            Test Decision Logic
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TestDecisionSection config={localConfig} />
        </AccordionDetails>
      </Accordion>

      {/* Configuration Summary */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" fontWeight="bold" color="text.secondary">
          CONFIGURATION SUMMARY
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">
            • Model: <strong>{modelOptions.find(m => m.id === localConfig.model)?.displayName || localConfig.model}</strong>
          </Typography>
          <Typography variant="body2">
            • Routes: <strong>{localConfig.routes.length}</strong> ({localConfig.routes.map(r => r.name).join(', ')})
          </Typography>
          <Typography variant="body2">
            • Default Route: <strong>{localConfig.defaultRoute ? localConfig.routes.find(r => r.id === localConfig.defaultRoute)?.name : 'None'}</strong>
          </Typography>
          <Typography variant="body2">
            • Input Limit: <strong>{localConfig.maxInputTokens ? `${localConfig.maxInputTokens} tokens` : 'Unlimited'}</strong>
          </Typography>
        </Box>
      </Box>

      {/* Prompt Optimization Modal */}
      <PromptOptimizationModal
        open={optimizationModalOpen}
        originalPrompt={localConfig.decisionPrompt}
        onClose={() => setOptimizationModalOpen(false)}
        onAccept={(optimizedPrompt) => handleConfigChange({ decisionPrompt: optimizedPrompt })}
      />
    </Box>
  );
};

export default DecisionBlockConfigPanel;


